import { useEffect } from 'react'

import { useRouter } from '@tribeplatform/react-sdk'
import { useSlate } from '@tribeplatform/react-slate-kit/hooks'
import { SectionHeader } from '@tribeplatform/react-ui-kit/SectionHeader'
import { Tabs } from '@tribeplatform/react-ui-kit/Tabs'

import { useMultiViewBlockQueryParam } from '../hooks/useMultiViewBlockQueryParam'
import { View } from '../types'

interface RenderProps<T> {
  view: T
  preview: boolean
}
interface MultiViewBlockProps<T> {
  activeViewIndex: number
  title: string
  description: string
  views: T[]
  linkUrl: string
  linkText: string
  children: (props: RenderProps<T>) => JSX.Element
}
export function MultiViewBlock<T extends View>({
  activeViewIndex,
  title,
  description,
  views,
  linkUrl,
  linkText,
  children,
}: MultiViewBlockProps<T>) {
  const { Link } = useRouter()
  const { mode } = useSlate()
  const preview = mode !== 'live'

  const [selectedIndex, setSelectedIndex] = useMultiViewBlockQueryParam({
    views,
  })

  useEffect(() => {
    if (preview) {
      setSelectedIndex(activeViewIndex)
    }
  }, [preview, activeViewIndex, setSelectedIndex])

  return (
    <div>
      {(title || description) && (
        <div className="flex justify-between items-end">
          <SectionHeader
            className="flex-1"
            title={title ?? ''}
            description={description}
          />
          {views.length <= 1 && (
            <Link
              href={linkUrl ?? ''}
              variant="accent"
              className="pb-5 mx-2 sm:mx-0"
            >
              {linkText}
            </Link>
          )}
        </div>
      )}
      <>
        {views.length > 1 ? (
          <Tabs.Group
            defaultIndex={selectedIndex}
            onChange={setSelectedIndex}
            // adding a key to force rerender if in customizer
            {...(mode !== 'live' ? { key: selectedIndex } : {})}
          >
            <div className="flex justify-between items-center">
              <Tabs.List variant="pills" size="md" className="flex-1">
                {views.map((view, index) => (
                  // we have to use array index as the key to track reorders
                  // eslint-disable-next-line react/no-array-index-key
                  <Tabs.Tab key={index} name={view.id}>
                    {view.name}
                  </Tabs.Tab>
                ))}
              </Tabs.List>
              <Link
                href={linkUrl ?? ''}
                variant="accent"
                className="mx-2 sm:mx-0"
              >
                {linkText}
              </Link>
            </div>
            <Tabs.Panels>
              {views.map(view => (
                <Tabs.Panel key={view.id}>
                  {children({ view, preview })}
                </Tabs.Panel>
              ))}
            </Tabs.Panels>
          </Tabs.Group>
        ) : (
          <>
            {!title && !description && !!linkText && (
              <div className="flex justify-end pb-5">
                <Link
                  href={linkUrl ?? ''}
                  variant="accent"
                  className="mx-2 sm:mx-0"
                >
                  {linkText}
                </Link>
              </div>
            )}
            {views.length === 1 ? (
              <>
                {children({
                  view: views[0],
                  preview,
                })}
              </>
            ) : (
              <>{children({ view: undefined, preview })}</>
            )}
          </>
        )}
      </>
    </div>
  )
}
